import React from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';

import { AuthProvider } from './context/AuthContext';
import ProtectedRoute   from './components/ProtectedRoute';
import AppLayout        from './components/layout/AppLayout';

// Auth pages
import Login          from './pages/auth/Login';
import Signup         from './pages/auth/Signup';
import ForgotPassword from './pages/auth/ForgotPassword';
import ResetPassword  from './pages/auth/ResetPassword';
import VerifyEmail    from './pages/auth/VerifyEmail';

// Onboarding
import Onboarding from './pages/onboarding/Onboarding';

// Dashboard pages
import Dashboard   from './pages/dashboard/Dashboard';
import CalendarPage from './pages/calendar/Calendar';
import Appointments from './pages/bookings/Appointments';
import Clients      from './pages/clients/Clients';
import ClientDetail from './pages/clients/ClientDetail';
import Services    from './pages/services/Services';
import Staff       from './pages/staff/Staff';
import TimeOff     from './pages/timeoff/TimeOff';
import Reports     from './pages/reports/Reports';
import Settings    from './pages/settings/Settings';
import Embed       from './pages/embed/Embed';
import Messages    from './pages/messages/Messages';

// Placeholder for pages built in later phases
const Soon = ({ title }) => (
  <div className="p-6">
    <div className="card p-10 text-center max-w-md">
      <h2 className="text-lg font-semibold text-ink mb-2">{title}</h2>
      <p className="text-sm text-ink-secondary">Coming in a later phase.</p>
    </div>
  </div>
);

const queryClient = new QueryClient({
  defaultOptions: {
    queries: { staleTime: 30_000, retry: 1, refetchOnWindowFocus: false },
  },
});

// Wraps protected routes with the sidebar layout
function AppPage({ children }) {
  return (
    <ProtectedRoute>
      <AppLayout>{children}</AppLayout>
    </ProtectedRoute>
  );
}

export default function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <AuthProvider>
        <BrowserRouter>
          <Routes>
            {/* Public auth routes */}
            <Route path="/login"           element={<Login />} />
            <Route path="/signup"          element={<Signup />} />
            <Route path="/forgot-password" element={<ForgotPassword />} />
            <Route path="/reset-password"  element={<ResetPassword />} />
            <Route path="/verify-email"    element={<VerifyEmail />} />

            {/* Onboarding (protected but no sidebar) */}
            <Route path="/onboarding" element={
              <ProtectedRoute><Onboarding /></ProtectedRoute>
            } />

            {/* Protected app routes */}
            <Route path="/dashboard"       element={<AppPage><Dashboard /></AppPage>} />
            <Route path="/calendar"        element={<AppPage><CalendarPage /></AppPage>} />
            <Route path="/appointments"    element={<AppPage><Appointments /></AppPage>} />
            <Route path="/services"        element={<AppPage><Services /></AppPage>} />
            <Route path="/staff"           element={<AppPage><Staff /></AppPage>} />
            <Route path="/time-off"        element={<AppPage><TimeOff /></AppPage>} />
            <Route path="/clients"         element={<AppPage><Clients /></AppPage>} />
            <Route path="/clients/:id"     element={<AppPage><ClientDetail /></AppPage>} />
            <Route path="/reports"         element={<AppPage><Reports /></AppPage>} />
            <Route path="/messages"        element={<AppPage><Messages /></AppPage>} />
            <Route path="/settings"        element={<AppPage><Settings /></AppPage>} />
            <Route path="/embed"           element={<AppPage><Embed /></AppPage>} />
            <Route path="/reports"      element={<AppPage><Soon title="Reports" /></AppPage>} />
            <Route path="/embed"        element={<AppPage><Soon title="Embed Widget" /></AppPage>} />
            <Route path="/settings"     element={<AppPage><Soon title="Settings" /></AppPage>} />

            {/* Default redirect */}
            <Route path="/"  element={<Navigate to="/dashboard" replace />} />
            <Route path="*"  element={<Navigate to="/dashboard" replace />} />
          </Routes>
        </BrowserRouter>
      </AuthProvider>
    </QueryClientProvider>
  );
}
